import { Check, ChevronLeft, ChevronRight, Download, Pause, Play, X } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'sonner'

import { entrarFullscreen, sairFullscreen } from './fullscreen'
import { PalcoFit } from './palco-fit'
import { useApresentacao } from './use-apresentacao'

import type { BlocoApresentavel } from './types'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/cn'

const DURACOES = [
  { s: 15, rotulo: '15s' },
  { s: 20, rotulo: '20s' },
  { s: 30, rotulo: '30s' },
  { s: 0, rotulo: 'Manual' },
] as const

// Identidade executiva do telão: navy profundo + dourado + creme (ref. Enjoy
// Business). Sobrescreve os tokens do tema para todo o overlay.
const TEMA_APRESENTACAO = {
  '--background': '234 48% 18%',
  '--foreground': '43 26% 94%',
  '--card': '234 38% 24%',
  '--muted': '234 26% 32%',
  '--muted-foreground': '40 14% 72%',
  '--primary': '43 82% 68%',
  '--primary-foreground': '234 48% 14%',
  '--border': '234 24% 38%',
  '--success': '150 45% 52%',
  '--destructive': '0 65% 62%',
  backgroundColor: 'hsl(234 48% 18%)',
  backgroundImage:
    'radial-gradient(circle at 38% 12%, hsl(43 82% 68% / 0.06), transparent 34%), radial-gradient(circle at 84% 78%, hsl(160 40% 30% / 0.12), transparent 38%)',
} as CSSProperties

export interface ModoApresentacaoProps {
  blocos: BlocoApresentavel[]
  /** Controles extras na barra inferior da apresentação (ex.: seletor de período). */
  controles?: ReactNode
}

export function ModoApresentacao({
  blocos,
  controles,
}: ModoApresentacaoProps): ReactElement | null {
  const aberta = useApresentacao((s) => s.aberta)
  const fechar = useApresentacao((s) => s.fechar)

  const containerRef = useRef<HTMLDivElement>(null)
  const [fase, setFase] = useState<'selecao' | 'tv'>('selecao')
  const [selecionados, setSelecionados] = useState<ReadonlySet<string>>(new Set())
  const [dur, setDur] = useState(20)
  const [idx, setIdx] = useState(0)
  const [tocando, setTocando] = useState(true)
  const [exportando, setExportando] = useState(false)

  // Ao ABRIR, começa do zero: todos os blocos marcados, na tela de seleção. Não
  // depende de `blocos`: trocar o período recria os blocos e não deve jogar o
  // usuário de volta pra seleção no meio da apresentação.
  useEffect(() => {
    if (!aberta) return
    setFase('selecao')
    setSelecionados(new Set(blocos.map((b) => b.id)))
    setIdx(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberta])

  const ativos = blocos.filter((b) => selecionados.has(b.id))
  const gira = ativos.length > 1

  const sair = useCallback((): void => {
    void sairFullscreen()
    fechar()
  }, [fechar])

  const avancar = useCallback((): void => {
    setIdx((i) => (i + 1) % ativos.length)
  }, [ativos.length])

  const voltar = useCallback((): void => {
    setIdx((i) => (i - 1 + ativos.length) % ativos.length)
  }, [ativos.length])

  function iniciar(): void {
    setIdx(0)
    setTocando(dur > 0)
    setFase('tv')
    void entrarFullscreen(containerRef.current)
  }

  // Espera o slide recém-trocado montar e as queries dele resolverem — o
  // skeleton (animate-pulse) some quando o dado chega. Teto de 20s por slide:
  // fonte lenta não pode travar a exportação pra sempre.
  async function aguardarSlidePronto(alvo: HTMLElement): Promise<void> {
    await new Promise((r) => setTimeout(r, 250))
    const limite = Date.now() + 20_000
    while (Date.now() < limite && alvo.querySelector('[class*="animate-pulse"]') !== null) {
      await new Promise((r) => setTimeout(r, 250))
    }
    await new Promise((r) => setTimeout(r, 250))
  }

  // Percorre TODOS os slides ativos — e, dentro de cada um, TODAS as abas
  // internas (os botões `aria-pressed` do próprio slide) — capturando cada
  // tela como imagem (fiel ao tema, clip-path e gradientes) num único PDF,
  // uma página por tela, do tamanho exato do slide. As libs entram por import
  // dinâmico: só quem exporta paga esse peso.
  async function exportarPdf(): Promise<void> {
    // O botão só existe com o overlay montado; `disabled` segura o reentrante.
    const alvo = containerRef.current!
    const idxOriginal = idx
    setExportando(true)
    setTocando(false)
    try {
      const [{ toCanvas }, { jsPDF }] = await Promise.all([
        import('html-to-image'),
        import('jspdf'),
      ])
      const telas: HTMLCanvasElement[] = []
      const capturar = async (): Promise<void> => {
        telas.push(
          await toCanvas(alvo, {
            pixelRatio: 2,
            backgroundColor: 'hsl(234 48% 18%)',
            // A barra de controles não pertence ao slide exportado.
            filter: (no) => !(no instanceof HTMLElement && no.dataset.exportIgnore === 'true'),
          }),
        )
      }
      for (let i = 0; i < ativos.length; i++) {
        setIdx(i)
        await aguardarSlidePronto(alvo)
        // Abas internas do slide (fora da barra de controles, que também usa
        // aria-pressed no seletor de dia/período).
        const abas = [...alvo.querySelectorAll<HTMLButtonElement>('button[aria-pressed]')].filter(
          (botao) => botao.closest('[data-export-ignore="true"]') === null,
        )
        if (abas.length > 1) {
          for (const abaBotao of abas) {
            abaBotao.click()
            await aguardarSlidePronto(alvo)
            await capturar()
          }
          // Devolve o slide pra primeira aba antes de seguir.
          abas[0]?.click()
        } else {
          await capturar()
        }
      }
      const primeira = telas[0]!
      const pdf = new jsPDF({
        orientation: primeira.width >= primeira.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [primeira.width, primeira.height],
        hotfixes: ['px_scaling'],
      })
      telas.forEach((tela, i) => {
        if (i > 0) {
          pdf.addPage([tela.width, tela.height], tela.width >= tela.height ? 'landscape' : 'portrait')
        }
        pdf.addImage(tela.toDataURL('image/png'), 'PNG', 0, 0, tela.width, tela.height)
      })
      const dia = new Date().toISOString().slice(0, 10)
      pdf.save(`telao-apresentacao-${dia}.pdf`)
      toast.success(`PDF exportado com ${telas.length} tela(s).`)
    } catch (err) {
      console.error('[ModoApresentacao] exportar pdf', err)
      toast.error('Não foi possível gerar o PDF. Tente de novo.')
    } finally {
      setIdx(idxOriginal)
      setExportando(false)
    }
  }

  function alternar(id: string): void {
    setSelecionados((prev) => {
      const prox = new Set(prev)
      if (prox.has(id)) prox.delete(id)
      else prox.add(id)
      return prox
    })
  }

  // Auto-advance entre blocos (só faz sentido com mais de um bloco).
  useEffect(() => {
    if (fase !== 'tv' || !gira || !tocando || dur === 0) return
    const timer = setTimeout(avancar, dur * 1000)
    return () => clearTimeout(timer)
  }, [fase, gira, tocando, dur, idx, avancar])

  // Atalhos de teclado durante a apresentação.
  useEffect(() => {
    if (!aberta) return
    function aoTeclar(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        sair()
        return
      }
      if (fase !== 'tv') return
      if (e.key === 'ArrowRight') avancar()
      else if (e.key === 'ArrowLeft') voltar()
      else if (e.key === ' ') {
        e.preventDefault()
        setTocando((v) => !v)
      }
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [aberta, fase, avancar, voltar, sair])

  if (!aberta) return null

  const ativo = ativos[idx]!

  // Portal para o body: um ancestral com transform/filter no shell viraria o
  // bloco de contenção do `fixed`, e o overlay não cobriria o topo da tela.
  return createPortal(
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-label="Modo apresentação"
      style={TEMA_APRESENTACAO}
      className="text-foreground dark fixed inset-0 z-50 flex flex-col overflow-hidden"
    >
      {fase === 'selecao' ? (
        <div className="m-auto w-full max-w-3xl px-6 py-8">
          <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">Apresentar</h2>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">
            Escolha os blocos de relatório. Cada bloco vira uma tela cheia — pronto para a reunião.
          </p>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {blocos.map((bloco) => {
              const marcado = selecionados.has(bloco.id)
              const Icone = bloco.icone
              return (
                <button
                  key={bloco.id}
                  type="button"
                  onClick={() => alternar(bloco.id)}
                  aria-pressed={marcado}
                  className={cn(
                    'bg-card relative rounded-xl border p-4 text-left transition-colors',
                    marcado
                      ? 'border-primary ring-primary/40 ring-1'
                      : 'border-border hover:border-primary/50',
                  )}
                >
                  <span
                    className={cn(
                      'absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full border',
                      marcado
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border',
                    )}
                  >
                    {marcado ? <Check className="h-3 w-3" aria-hidden /> : null}
                  </span>
                  <span className="bg-muted mb-3 flex h-10 w-10 items-center justify-center rounded-lg">
                    <Icone className="h-5 w-5" aria-hidden />
                  </span>
                  <span className="block font-semibold">{bloco.titulo}</span>
                </button>
              )
            })}
          </div>

          <p className="text-muted-foreground mt-3 text-xs">Mais blocos em breve.</p>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm">Girar a cada</span>
              <div className="bg-muted flex gap-1 rounded-lg p-1">
                {DURACOES.map((d) => (
                  <button
                    key={d.s}
                    type="button"
                    onClick={() => setDur(d.s)}
                    aria-pressed={dur === d.s}
                    className={cn(
                      'min-h-[44px] rounded-md px-3 text-sm font-medium transition-colors sm:min-h-9',
                      dur === d.s
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {d.rotulo}
                  </button>
                ))}
              </div>
            </div>
            <div className="ml-auto flex gap-2">
              <Button type="button" variant="outline" onClick={sair}>
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={iniciar}
                disabled={ativos.length === 0}
                className="gap-2"
              >
                <Play className="h-4 w-4" aria-hidden />
                Iniciar
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="min-h-0 flex-1 overflow-hidden p-3 sm:p-5">
            <PalcoFit>{ativo.conteudo}</PalcoFit>
          </div>

          <div
            data-export-ignore="true"
            className="border-border flex shrink-0 items-center gap-3 border-t px-3 py-2 sm:px-5"
          >
            {gira ? (
              <div className="border-border bg-card/70 flex items-center gap-1 rounded-full border p-1.5">
                <button
                  type="button"
                  onClick={voltar}
                  aria-label="Bloco anterior"
                  className="hover:bg-muted flex h-11 w-11 items-center justify-center rounded-full"
                >
                  <ChevronLeft className="h-5 w-5" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => setTocando((v) => !v)}
                  aria-label={tocando ? 'Pausar' : 'Retomar'}
                  className="bg-primary text-primary-foreground flex h-11 w-11 items-center justify-center rounded-full"
                >
                  {tocando ? (
                    <Pause className="h-5 w-5" aria-hidden />
                  ) : (
                    <Play className="h-5 w-5" aria-hidden />
                  )}
                </button>
                <button
                  type="button"
                  onClick={avancar}
                  aria-label="Próximo bloco"
                  className="hover:bg-muted flex h-11 w-11 items-center justify-center rounded-full"
                >
                  <ChevronRight className="h-5 w-5" aria-hidden />
                </button>
                <span className="text-muted-foreground min-w-[4.5rem] text-center text-sm tabular-nums">
                  {ativo.titulo} · {idx + 1}/{ativos.length}
                </span>
              </div>
            ) : (
              <span className="text-muted-foreground text-sm font-medium">{ativo.titulo}</span>
            )}
            <div className="flex min-w-0 flex-1 justify-center">
              {ativo.controles ?? controles}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => void exportarPdf()}
              disabled={exportando}
              className="gap-2"
            >
              <Download className="h-4 w-4" aria-hidden />
              {exportando ? 'Gerando…' : 'Exportar PDF'}
            </Button>
            <Button type="button" variant="outline" onClick={sair} className="gap-2">
              <X className="h-4 w-4" aria-hidden />
              Sair
            </Button>
          </div>
        </>
      )}
    </div>,
    document.body,
  )
}
